// --- ANIMACIÓN DE CONTADORES (bloque stats) ---
import { store } from '../core/store.js';
import { registerPostRender } from '../core/render.js';
import { animate } from '../vendor/anime.esm.js';

import { DURATION, EASE, resolveDuration } from './motion-tokens.js';

/**
 * Parsea un string como "500+", "99%", "10K+", "4.9★", "$5M" y devuelve { prefix, number, suffix }
 */
function parseCounterValue(rawStr) {
    const str = String(rawStr).trim();
    // Detectar multiplicadores de magnitud (K, M, B)
    const multipliers = { 'K': 1000, 'M': 1000000, 'B': 1000000000 };

    // Mejorado para aceptar comas y puntos como separadores (y ahora 'B')
    const match = str.match(/^([^0-9]*)([\d.,]+)([KMB]?)([^0-9]*)$/i);
    if (!match) return null;

    const prefix = match[1] || '';
    let rawNumber = match[2];
    const mag = match[3].toUpperCase();
    const suffix = match[4] || '';

    // Detectar si la coma/punto es separador de miles o decimal:
    // - "52,400" o "52.400" → miles (3 dígitos tras separador)
    // - "4,5" o "4.5" → decimal (1-2 dígitos tras separador)
    let number;
    let useThousands = false;
    const commaMatch = rawNumber.match(/^([\d.]+),(\d+)$/);
    const dotMatch = rawNumber.match(/^([\d,]+)\.(\d+)$/);

    if (commaMatch && commaMatch[2].length === 3) {
        // Coma como separador de miles: "52,400" → 52400
        number = parseFloat(rawNumber.replace(/,/g, ''));
        useThousands = true;
    } else if (dotMatch && dotMatch[2].length === 3) {
        // Punto como separador de miles (español): "52.400" → 52400
        number = parseFloat(rawNumber.replace(/\./g, ''));
        useThousands = true;
    } else {
        // Separador decimal normal
        number = parseFloat(rawNumber.replace(',', '.'));
    }

    if (isNaN(number)) return null;

    if (multipliers[mag]) {
        return { prefix, number, suffix: mag + suffix, raw: str, useThousands };
    }
    return { prefix, number, suffix, raw: str, useThousands };
}

/**
 * Formatea número con separador de miles (punto en español)
 */
function formatNumber(num, decimals, useThousands) {
    if (useThousands && decimals === 0) {
        return num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
    return num.toFixed(decimals);
}

/**
 * Anima un único elemento de stat.
 * @param {HTMLElement} el  - El elemento con el número visible
 */
export function animateSingleCounter(el) {
    // Omitir animación si hay saltos de línea o HTML complejo para no romper la estructura
    if (el.innerHTML.includes('<br') || el.innerHTML.includes('<div') || el.innerHTML.includes('<p')) {
        return;
    }

    // Buscar el nodo más profundo para mantener los <span> de color
    let target = el;
    while (target.children.length === 1 && target.childNodes.length === 1) {
        target = target.children[0];
    }

    // PROTECCIÓN ESTRICTA DE HTML
    if (target.innerHTML !== target.textContent) {
        return;
    }

    const parsed = parseCounterValue(target.textContent);
    if (!parsed) return;

    const { prefix, number, suffix, useThousands } = parsed;
    if (number === 0 && !target.textContent.match(/\d/)) return;

    const isFloat = !useThousands && number % 1 !== 0;
    const decimals = isFloat ? 1 : 0;
    
    // GUARDAR EL VALOR HTML ORIGINAL ANTES DE ALTERARLO
    if (!el.hasAttribute('data-counter-val')) {
        el.setAttribute('data-counter-val', el.innerHTML);
    }

    // Asegurarse de que el target es solo el texto, por si hay otros nodos
    const textNode = document.createTextNode(prefix + formatNumber(0, decimals, useThousands) + suffix);
    target.textContent = '';
    target.appendChild(textNode);

    const duration = resolveDuration(DURATION.slow * 2.5);
    const endVal = number;

    const obj = { val: 0 };
    animate(obj, {
        val: endVal,
        duration: duration,
        ease: EASE.snappy, // Equivalent to outExpo
        onUpdate: () => {
            textNode.nodeValue = prefix + formatNumber(obj.val, decimals, useThousands) + suffix;
        },
        onComplete: () => {
            textNode.nodeValue = prefix + formatNumber(endVal, decimals, useThousands) + suffix;
        }
    });
}

/**
 * Inicializa los contadores del bloque stats con IntersectionObserver.
 * Solo anima UNA vez por elemento.
 */
export function initCounters() {
    if (store.get('editorMode')) return;
    const statNumbers = document.querySelectorAll('#bloque-stats [data-field="name"]');
    if (!statNumbers.length) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && !entry.target.dataset.animated) {
                entry.target.dataset.animated = 'true';
                animateSingleCounter(entry.target);
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.1 });

    statNumbers.forEach(el => observer.observe(el));
}

registerPostRender(initCounters, { previewOnly: true });
